import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { wbp, reduceResolution } from 'Theme';

const useStyles = makeStyles<Theme, { noToolbar: boolean }>((theme: Theme) =>
    createStyles({
        root: {
            '& .ql-container.ql-snow': {
                [theme.breakpoints.down(wbp)]: {
                    height: `${reduceResolution(500)}px !important`
                },
                [theme.breakpoints.up(wbp)]: {
                    height: '500px !important'
                },
                borderRadius: ({ noToolbar }) => (noToolbar ? '10px' : '0px 0px 10px 10px')
            },
            '& .ql-toolbar': {
                borderRadius: '10px 10px 0px 0px',
                [theme.breakpoints.down('sm')]: {
                    '& > .ql-formats': {
                        'marginRight': 8,
                        '& > button': {
                            width: 22
                        }
                    }
                }
            },
            '& .ql-container': {
                borderRadius: '0px 0px 10px 10px'
            },
            '& .ql-editor': {
                'minHeight': 400,
                'maxWidth': 'calc(100vw - 50px)',
                'wordBreak': 'break-word',
                'overflowWrap': 'break-word',

                '& *': {
                    lineHeight: 1.4,
                    fontFamily: 'Poppins, sans-serif'
                },
                '& h1': {
                    fontSize: 28,
                    fontWeight: 600,
                    marginBottom: 10
                },
                '& h2': {
                    fontSize: 24,
                    fontWeight: 600,
                    marginBottom: 8
                },
                '& p, & ul, & ol': {
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: 1.4,
                    paddingLeft: '0px'
                },
                '& li': {
                    marginBottom: '1rem'
                },
                '& .ql-size-large': {
                    fontSize: '1.25rem'
                },
                '& .ql-size-huge': {
                    fontSize: '1.5rem'
                }
            }
        }
    })
);

interface Props {
    value?: string;
    onChange?: (value: string, plainText: string, length: number) => void;
    toolbar?: boolean | string | { [format: string]: any } | (string | { [format: string]: any })[];
    resetKey?: string | number;
    maxLength?: number;
}

export interface QuillEditorRef {
    getEditor: () => Quill | null;
    getCursorPosition: () => number | null;
}

const QuillEditor = forwardRef<QuillEditorRef, Props>((props, ref) => {
    const noToolbar = props.toolbar === false;
    const classes = useStyles({ noToolbar });

    const container = useRef<HTMLDivElement>(null);
    const [editor, setEditor] = useState<Quill | null>(null);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
        getEditor: () => editor,
        getCursorPosition: () => cursorPosition
    }));

    const getDeltaTextLength = (delta: Quill.DeltaStatic): number => {
        return (
            delta.ops?.reduce((acc, op) => {
                if (typeof op.insert === 'string') {
                    return acc + op.insert.length;
                }
                return acc;
            }, 0) ?? 0
        );
    };

    const truncateDelta = (delta: Quill.DeltaStatic, maxLength: number): Quill.DeltaStatic => {
        const Delta = Quill.import('delta');
        const newDelta = new Delta();
        let count = 0;

        delta.ops?.forEach(op => {
            if (count >= maxLength) return;

            if (typeof op.insert === 'string') {
                const remaining = maxLength - count;
                const insertText = op.insert.slice(0, remaining);
                newDelta.insert(insertText, op.attributes);
                count += insertText.length;
            } else {
                newDelta.insert(op.insert, op.attributes);
            }
        });

        return newDelta;
    };

    useEffect(() => {
        if (!container.current || editor) return;

        const currEditor = new Quill(container.current, {
            modules: {
                toolbar:
                    props.toolbar !== undefined
                        ? props.toolbar
                        : [
                              [{ size: [] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ color: [] }, { background: [] }],
                              [{ script: 'super' }, { script: 'sub' }],
                              [{ header: '1' }, { header: '2' }],
                              [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                              [{ align: [] }],
                              ['link'],
                              ['clean']
                          ],
                clipboard: {
                    matchers: [
                        [
                            'img',
                            () => {
                                return { ops: [] };
                            }
                        ]
                    ]
                }
            },
            placeholder: '',
            theme: 'snow'
        });

        currEditor.on('text-change', () => {
            const fullDelta = currEditor.getContents();
            const length = getDeltaTextLength(fullDelta);

            if (props.maxLength !== undefined && length > props.maxLength) {
                const limitedDelta = truncateDelta(fullDelta, props.maxLength);
                currEditor.setContents(limitedDelta, 'silent');
                currEditor.setSelection(props.maxLength, 0, 'silent');

                const html = container.current?.querySelector('.ql-editor')?.innerHTML || '';
                const plainText = currEditor.getText();
                props.onChange?.(html, plainText, props.maxLength);
                return;
            }

            const html = container.current?.querySelector('.ql-editor')?.innerHTML || '';
            const plainText = currEditor.getText();
            props.onChange?.(html, plainText, length);
        });

        currEditor.on('selection-change', range => {
            if (range && range.index !== undefined) {
                setCursorPosition(range.index);
            }
        });

        currEditor.root.addEventListener('keydown', (e: KeyboardEvent) => {
            const length = getDeltaTextLength(currEditor.getContents());
            if (props.maxLength !== undefined && length >= props.maxLength) {
                if (e.key.length === 1 || e.key === ' ') {
                    e.preventDefault();
                }
            }
        });

        setEditor(currEditor);
    }, []);

    useEffect(() => {
        if (!editor || props.value === undefined) return;

        const delta = editor.clipboard.convert(props.value);
        editor.setContents(delta, 'silent');
        editor.setSelection(0, 0, 'silent');

        const html = container.current?.querySelector('.ql-editor')?.innerHTML || '';
        const plainText = editor.getText();
        const fullDelta = editor.getContents();
        const length =
            fullDelta.ops?.reduce((acc, op) => (typeof op.insert === 'string' ? acc + op.insert.length : acc), 0) ?? 0;

        props.onChange?.(html, plainText, length);
    }, [editor, props.resetKey]);

    return (
        <Box className={classes.root}>
            <div
                id="container"
                ref={container}
            ></div>
        </Box>
    );
});

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
